import { useState } from 'react';
import { z } from 'zod';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '@tanstack/react-form';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { SelectItem } from '@/common/components/ui/select';
import { Button } from '@/common/components/ui/button';
import { useCountries } from '@/features/auth/hooks/useCountries';
import { supplyPointQueries, supplyPointQueryKeys } from '@/features/supply-points/queries';
import { SupplyPointService } from '@/features/supply-points/services/SupplyPointService';
import { useSuspenseQuery } from '@tanstack/react-query';
import { MapPin, Cpu } from 'lucide-react';
import { FormLocationField } from '@/features/geolocation/components/form-location-field/FormLocationField';
import { EMPTY_LOCATION_VALUE } from '@/features/geolocation/types/location';
import { toSupplyPointGeoPayload } from '@/features/supply-points/mappers/toSupplyPointGeoPayload';

// ─── Form schema ──────────────────────────────────────────────────────────────

const step1Schema = z.object({
  name: requiredInput(z.string().min(1).max(255)),
  street: requiredInput(z.string().min(1).max(255)),
  city: requiredInput(z.string().min(1).max(100)),
  zipCode: requiredInput(z.string().min(1).max(20)),
  country: requiredInput(z.string()),
  systemType: requiredInput(z.coerce.number().int().positive()),
  // The position is optional — a missing one only degrades production prediction.
  location: z
    .object({
      latitude: z.number().min(-90).max(90).nullable(),
      longitude: z.number().min(-180).max(180).nullable(),
      altitude: z.number().min(-9999).max(9999).nullable(),
      source: z.enum(['stored', 'geocoded', 'manual']).nullable(),
      altitudeSource: z.enum(['auto', 'manual']).nullable(),
    })
    .refine((value) => (value.latitude === null) === (value.longitude === null), {
      // A function, so the message is translated when validation runs rather than
      // at module load, before a locale is active.
      error: () => t`Latitude and longitude must be set together`,
    }),
});

// ─── Component ────────────────────────────────────────────────────────────────

interface CreateSupplyPointStep1Props {
  orgId?: number;
  onCreated: (supplyPointId: number, name: string) => void;
  onCancel: () => void;
}

export function CreateSupplyPointStep1({
  orgId,
  onCreated,
  onCancel,
}: CreateSupplyPointStep1Props) {
  const queryClient = useQueryClient();
  const { data: types } = useSuspenseQuery(supplyPointQueries.types());
  const countries = useCountries();
  /** Bumped on address-field blur; arms the picker's debounced auto-locate. */
  const [locateSignal, setLocateSignal] = useState(0);
  const armLocate = () => setLocateSignal((signal) => signal + 1);

  const form = useAppForm(
    appFormOptions({
      schema: step1Schema,
      defaultValues: {
        name: null,
        street: null,
        city: null,
        zipCode: null,
        country: 'CZ',
        systemType: null,
        location: EMPTY_LOCATION_VALUE,
      },
      onSubmit: async ({ value }) => {
        const newSP = await SupplyPointService.create({
          name: value.name,
          street: value.street,
          city: value.city,
          zipCode: value.zipCode,
          country: value.country,
          systemType: value.systemType,
          ...toSupplyPointGeoPayload(value.location),
        });

        if (orgId) {
          await SupplyPointService.addPartners(newSP.id, [orgId]);
        }

        await queryClient.invalidateQueries({ queryKey: supplyPointQueryKeys.all });
        onCreated(newSP.id, newSP.name);
      },
    })
  );

  // Primitive subscriptions, so the picker's address prop is stable until one of
  // these four fields actually changes.
  const street = useStore(form.store, (state) => state.values.street);
  const city = useStore(form.store, (state) => state.values.city);
  const zipCode = useStore(form.store, (state) => state.values.zipCode);
  const country = useStore(form.store, (state) => state.values.country);

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-6 py-5 border-b border-border">
        <h2 className="text-base font-semibold">
          <Trans>Create supply point</Trans>
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          <Trans>
            Enter name, address and technical configuration. The supply point will be created
            immediately upon confirmation.
          </Trans>
        </p>
      </div>

      <form.AppForm>
        <form.FormElement>
          <div className="px-6 py-6 space-y-6">
            <FormErrors />

            {/* Location */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-muted-foreground" />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <Trans>Location</Trans>
                </span>
              </div>

              <form.AppField name="name">
                {(field) => (
                  <field.TextField
                    label={<Trans>Supply point name</Trans>}
                    placeholder={t`e.g. Main Prague Office`}
                    required
                  />
                )}
              </form.AppField>

              <form.AppField name="street" listeners={{ onBlur: () => armLocate() }}>
                {(field) => (
                  <field.TextField
                    label={<Trans>Street and number</Trans>}
                    placeholder={t`Václavské náměstí 1`}
                    required
                  />
                )}
              </form.AppField>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <form.AppField name="city" listeners={{ onBlur: () => armLocate() }}>
                  {(field) => (
                    <field.TextField label={<Trans>City</Trans>} placeholder={t`Prague`} required />
                  )}
                </form.AppField>

                <form.AppField name="zipCode" listeners={{ onBlur: () => armLocate() }}>
                  {(field) => (
                    <field.TextField
                      label={<Trans>Postal code</Trans>}
                      placeholder={t`110 00`}
                      required
                    />
                  )}
                </form.AppField>

                <div className="col-span-2 sm:col-span-1">
                  {/* A select never blurs on the way to a new value — the
                      choice itself is the moment the field settled, so this
                      arms on onChange rather than pretending it's a blur. */}
                  <form.AppField name="country" listeners={{ onChange: () => armLocate() }}>
                    {(field) => (
                      <field.SelectField label={<Trans>Country</Trans>} required>
                        {countries.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </field.SelectField>
                    )}
                  </form.AppField>
                </div>
              </div>

              <form.AppField name="location">
                {() => (
                  <FormLocationField
                    label={<Trans>Exact position</Trans>}
                    description={
                      <Trans>
                        Check the position on the map and correct it if needed. It is used for
                        production prediction.
                      </Trans>
                    }
                    address={{
                      street,
                      city,
                      postalcode: zipCode,
                      country,
                    }}
                    locateSignal={locateSignal}
                  />
                )}
              </form.AppField>
            </div>

            <div className="border-t border-border" />

            {/* Technical configuration */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Cpu size={14} className="text-muted-foreground" />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <Trans>Technical configuration</Trans>
                </span>
              </div>

              <form.AppField name="systemType">
                {(field) => (
                  <field.SelectField label={<Trans>Integration type</Trans>} required>
                    {types.map((type) => (
                      <SelectItem key={type.id} value={String(type.id)}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </field.SelectField>
                )}
              </form.AppField>
            </div>
          </div>

          <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/30">
            <span className="text-xs text-muted-foreground">
              <Trans>Step 1 of 4 · Required</Trans>
            </span>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={onCancel}>
                <Trans>Cancel</Trans>
              </Button>
              <FormSubmit>
                <Trans>Create supply point</Trans>
              </FormSubmit>
            </div>
          </div>
        </form.FormElement>
      </form.AppForm>
    </div>
  );
}
